import React, { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useTranslation } from "react-i18next";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";

import { postNotification } from "../../../components/utilities/Notifications";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";

import { Formik, FieldArray } from "formik";
import { handlePagingAll } from "../../../components/api/APIUtilities";
import GenericPicker from "../../../components/utilities/GenericPicker";

import moment from "moment";
import "moment-timezone";

import ImageUploader, {
  uploadBas64ToS3,
} from "../../../components/utilities/ImageUploader";
import {
  BCPCircleCheck,
  BCPLockKeyhole,
  BCPLockKeyholeOpen,
  BCPXmark,
} from "../../../components/icons/bcp/BCPIcons";
import {
  getData,
  postData,
} from "../../../components/utilities/UtilityFunctions";

export default function EventJudgeReport(props) {
  const { event, authContext } = props;

  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventJudgeReport"
  });

  const [readOnly, setReadOnly] = useState(
    !event?.userRole?.permissions?.judgeReport &&
      !authContext?.authState?.identity?.isJudge
  );

  const [judgeReport, setJudgeReport] = useState(null);
  const [judges, setJudges] = useState(null);

  const [processing, setProcessing] = useState(false);
  const [subLoading, setSubLoading] = useState(true);

  const [submitOpen, setSubmitOpen] = useState(false);

  const [saveBoxRef, saveBoxInView] = useInView({
    threshold: 0,
  });

  useEffect(() => {
    const check = async () => {
      var apiEventRoles = await handlePagingAll(`/eventroles`, {
        limit: 100,
      });

      var apiEventRolesDict = {};
      apiEventRoles.forEach((apiEventRole) => {
        apiEventRolesDict[apiEventRole.id] = apiEventRole;
      });

      var eventUsers = event?.eventUsers ? { ...event.eventUsers } : {};

      for (let eventUser of Object.values(eventUsers)) {
        var apiUser = await getData(`/users/${eventUser.id}`, {});
        eventUsers[eventUser.id] = {
          ...eventUser,
          ...apiUser,
        };
      }

      var judgesList = Object.values(eventUsers)
        .filter((eventUser) => {
          return (
            apiEventRolesDict[eventUser.role?.id]?.permissions?.judgeReport ||
            eventUser.isJudge
          );
        })
        .map((eventUser) => {
          return {
            value: eventUser.id,
            label: `${eventUser.firstName} ${eventUser.lastName}`.trim(),
          };
        });

      setJudges(judgesList);

      var initialJudgeReport = {
        judgeId: authContext?.authState?.identity?.userId,
        judgeFirstName:
          authContext?.authState?.identity?.firstName ||
          authContext?.authState?.identity?.given_name,
        judgeLastName:
          authContext?.authState?.identity?.lastName ||
          authContext?.authState?.identity?.family_name,
        text: "",
        imageFiles: [],
        ...event?.judgeReport,
      };

      setJudgeReport(initialJudgeReport);

      setSubLoading(false);
    };
    check();
    // eslint-disable-next-line
  }, []);

  return (
    <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
      <Grid container sx={{ gap: 2 }}>
        <Grid
          item
          xs={12}
          sx={{
            p: 2,
          }}
        >
          <Typography variant="h3">{t("judgeReport")}</Typography>
        </Grid>
        {(authContext?.authState?.identity?.isAdmin === true ||
          authContext?.authState?.identity?.adminGameSystems?.includes(
            event?.gameSystemId
          )) && (
          <Grid
            item
            xs={12}
            sx={{
              p: 2,
            }}
          >
            <Typography variant="h5">
              <Button
                color="primary"
                variant="outlined"
                fullWidth
                onClick={() => {
                  setReadOnly(!readOnly);
                }}
                endIcon={
                  readOnly ? (
                    <BCPLockKeyhole outlined />
                  ) : (
                    <BCPLockKeyholeOpen outlined />
                  )
                }
              >
                {readOnly ? t("locked") : t("unlocked")}
              </Button>
            </Typography>
          </Grid>
        )}
        <Grid
          item
          xs={12}
          sx={{
            p: 2,
          }}
        >
          {!subLoading && (
            <Formik
              initialValues={judgeReport}
              onSubmit={async (values, actions) => {
                setProcessing(true);
                try {
                  var body;
                  var sanitizedValues = {
                    ...values,
                  };

                  if (sanitizedValues.submittedLeagues) {
                    delete sanitizedValues.submittedLeagues;
                  }

                  if (sanitizedValues.imageFiles?.length > 0) {
                    for (
                      var i = 0;
                      i < sanitizedValues.imageFiles.length;
                      i++
                    ) {
                      if (sanitizedValues.imageFiles[i].imageURL) {
                        // upload image to s3
                        var newImageFile = await uploadBas64ToS3(
                          sanitizedValues.imageFiles[i].imageURL
                        );
                        delete sanitizedValues.imageFiles[i].imageURL;
                        if (newImageFile.fileId) {
                          sanitizedValues.imageFiles[i].photoFileId =
                            newImageFile.fileId;
                        }
                      }
                    }
                  }

                  body = {
                    judgeReport: sanitizedValues,
                  };

                  var updateJudgeReport = await postData(
                    `/events/${event.id}/updateJudgeReport`,
                    {
                      body: body,
                    }
                  );

                  setJudgeReport(updateJudgeReport);
                  actions.resetForm({ values: updateJudgeReport });
                  postNotification("success", t("judgeReportSaved"));
                  setSubmitOpen(true);
                } catch (err) {
                  postNotification("error", err);
                }
                setProcessing(false);
                return;
              }}
            >
              {({
                errors,
                handleBlur,
                handleChange,
                handleSubmit,
                touched,
                values,
                setFieldValue,
                dirty,
              }) => (
                <form onSubmit={handleSubmit} noValidate>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      {readOnly ? (
                        <TextField
                          label={t("judge")}
                          value={`${values.judgeFirstName} ${values.judgeLastName}`.trim()}
                          variant="outlined"
                          fullWidth
                          margin="normal"
                          disabled={true}
                        />
                      ) : (
                        <GenericPicker
                          name="judgeId"
                          label={t("judge")}
                          value={values.judgeId}
                          handleChange={handleChange}
                          list={judges}
                          inclAny={false}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label={t("updated")}
                        value={moment(values.updatd_at).format(
                          "HH:mm MMM Do, YYYY"
                        )}
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        disabled={true}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="text"
                        label={t("report")}
                        value={values.text}
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={3}
                        margin="normal"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={Boolean(touched.text && errors.text)}
                        helperText={touched.text && errors.text}
                        disabled={readOnly}
                      />
                    </Grid>

                    {(!readOnly || values.imageFiles?.length > 0) && (
                      <Grid item xs={12}>
                        <Typography variant="body1" gutterBottom>
                          {t("gallery")}
                        </Typography>
                        <Grid container spacing={1}>
                          <FieldArray
                            name="imageFiles"
                            render={(arrayHelpers) => (
                              <>
                                {values.imageFiles?.length > 0 &&
                                  values.imageFiles.map((picture, rowIndex) => {
                                    return (
                                      <Grid item xs={12}>
                                        <ImageUploader
                                          label={t("image")}
                                          name={`imageFiles[${rowIndex}].imageURL`}
                                          fileId={
                                            values.imageFiles[rowIndex]
                                              .photoFileId || null
                                          }
                                          setFieldValue={setFieldValue}
                                          defaultUrl={
                                            values.imageFiles[rowIndex].photo
                                              ?.url ||
                                            values.imageFiles[rowIndex].imageURL
                                          }
                                          noAspect
                                          disabled={readOnly}
                                        />
                                      </Grid>
                                    );
                                  })}
                                {!readOnly && values.imageFiles?.length < 5 && (
                                  <Grid item xs={12}>
                                    <Button
                                      variant="outlined"
                                      color="primary"
                                      fullWidth
                                      onClick={() => arrayHelpers.push({})}
                                    >
                                      {t("addImage")}
                                    </Button>
                                  </Grid>
                                )}
                              </>
                            )}
                          />
                        </Grid>
                      </Grid>
                    )}

                    {!readOnly && (
                      <>
                        <Grid item xs={6}>
                          <Button
                            fullWidth
                            color="success"
                            variant="outlined"
                            onClick={() => {
                              if (dirty) {
                                postNotification(
                                  "warning",
                                  t("savePriorToSubmitting")
                                );
                                return;
                              }
                              if (!judgeReport.updated_at) {
                                postNotification(
                                  "warning",
                                  t("saveReportPriorToSubmitting")
                                );
                                return;
                              }
                              setSubmitOpen(true);
                            }}
                          >
                            {t("submitToCircuit")}
                          </Button>
                        </Grid>
                        <Grid item xs={6}>
                          <Button
                            ref={saveBoxRef}
                            fullWidth
                            color="success"
                            variant="contained"
                            type="submit"
                          >
                            {t("save")}
                          </Button>
                        </Grid>

                        {!saveBoxInView && (
                          <Box
                            sx={{
                              position: "fixed",
                              zIndex: 999,
                              p: 2,
                              bgcolor: "background.paper",
                              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                              boxShadow: 3,
                              bottom: 0,
                              left: [0],
                              width: "100%",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Button
                                fullWidth
                                color="success"
                                variant="contained"
                                type="submit"
                              >
                                {t("save")}
                              </Button>
                            </Box>
                          </Box>
                        )}
                      </>
                    )}
                  </Grid>
                </form>
              )}
            </Formik>
          )}
        </Grid>
      </Grid>

      {Boolean(subLoading || processing) && <ModalLoadingFull />}
      <SubmitModal
        judgeReport={judgeReport}
        setJudgeReport={setJudgeReport}
        event={event}
        setLoading={setProcessing}
        open={submitOpen}
        setOpen={setSubmitOpen}
        authContext={authContext}
      />
    </Container>
  );
}

function SubmitModal(props) {
  const {
    judgeReport,
    setJudgeReport,
    event,
    open,
    setOpen,
    setLoading,
    authContext,
  } = props;

  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventJudgeReport"
  });
  const [fetching, setFetching] = useState(true);
  const [leagues, setLeagues] = useState(null);
  const [tokenDict, setTokenDict] = useState(null);

  const submitToLeague = async (leagueId, tokenCode) => {
    try {
      setLoading(true);
      var body = {
        leagueId: leagueId,
      };
      if (tokenCode) {
        body.tokenCode = tokenCode;
      }
      var apiJudgeReport = await postData(
        `/events/${event.id}/submitJudgeReport`,
        {
          body: body,
        }
      );
      setJudgeReport(apiJudgeReport);
    } catch (err) {
      postNotification("error", err);
    }
    setLoading(false);
  };

  const fetch = async () => {
    setFetching(true);
    setTokenDict(null);
    setLeagues(null);
    var apiTokenDict = {};
    var apiEventTokens = await handlePagingAll("/leaguetokens", {
      eventId: event?.id,
    });

    if (apiEventTokens) {
      apiEventTokens.sort((a, b) => {
        if (a.status !== b.status) {
          if (a.status > b.status) return -1;
          if (b.status < a.status) return 1;
        }
        return moment(b.updated_at).diff(moment(a.updated_at));
      });
      apiEventTokens.forEach((apiEventToken) => {
        if (!apiTokenDict[apiEventToken.leagueId]) {
          apiTokenDict[apiEventToken.leagueId] = apiEventToken;
        }
      });
    }

    setTokenDict(apiTokenDict);

    var apiEventLeagues = await handlePagingAll(
      `/events/${event.id}/leagues`,
      {}
    );
    var apiDefaultLeagues = await handlePagingAll(`/leagues`, {
      bcpDefault: true,
      gameSystemId: event?.gameSystemId,
      active: true,
    });

    var apiLeagues = [];
    var leagueDict = {};
    apiEventLeagues.forEach((league) => {
      apiLeagues.push(league);
      leagueDict[league.id] = league;
    });
    apiDefaultLeagues.forEach((league) => {
      if (!leagueDict[league.id]) {
        apiLeagues.push(league);
        leagueDict[league.id] = league;
      }
    });

    if (apiLeagues?.length) {
      apiLeagues.sort((a, b) => {
        var nameA = a.name.toLowerCase();
        var nameB = b.name.toLowerCase();
        if (nameA > nameB) return 1;
        if (nameA < nameB) return -1;
        return 0;
      });
    }

    setLeagues(apiLeagues);
    setFetching(false);
  };

  useEffect(() => {
    fetch();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Modal
        disableAutoFocus
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: ["95vw", "85vw", 600],
            maxHeight: "95vh",
            overflowY: "scroll",
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4,
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={() => {
              setOpen(false);
            }}
          >
            <BCPXmark />
          </IconButton>
          {authContext?.authState?.identity?.isAdmin !== true &&
            !authContext?.authState?.identity?.adminGameSystems?.includes(
              event?.gameSystemId
            ) &&
            !event?.userRole?.permissions?.judgeReport &&
            !authContext?.authState?.identity?.isJudge && (
              <Grid container spacing={1} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="body1" paragraph>
                    {t("accessDeniedMessage")}
                  </Typography>
                </Grid>
              </Grid>
            )}
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="body1" sx={{ fontWeight: "600" }}>
                {t("circuits")}
              </Typography>
            </Grid>
            {fetching ? (
              <Grid item xs={12} sx={{ textAlign: "center" }}>
                <CircularProgress size={50} />
              </Grid>
            ) : leagues?.length > 0 ? (
              leagues.map((league, index) => {
                var color = judgeReport?.submittedLeagues?.[league.id]
                  ? "success"
                  : tokenDict?.[league.id]?.status === "used"
                  ? "primary"
                  : "warning";
                return (
                  <Grid key={index} item xs={12}>
                    <Button
                      variant="outlined"
                      fullWidth
                      color={color}
                      endIcon={color === "success" ? <BCPCircleCheck /> : null}
                      sx={{ textTransform: "unset" }}
                      disabled={
                        !event?.userRole?.permissions?.judgeReport &&
                        !authContext?.authState?.identity?.isJudge
                      }
                      onClick={async () => {
                        await submitToLeague(
                          league.id,
                          tokenDict?.[league.id]?.code
                        );
                      }}
                    >
                      {league.name}
                    </Button>
                    {judgeReport?.submittedLeagues?.[league.id]?.submitted && (
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: "0.75rem",
                          width: "100%",
                          textAlign: "center",
                        }}
                      >
                        {t("submitted")}{" "}
                        {moment(
                          judgeReport?.submittedLeagues?.[league.id]?.submitted
                        ).format("HH:mm MMM Do, YYYY")}
                      </Typography>
                    )}
                  </Grid>
                );
              })
            ) : (
              <Grid item xs={12}>
                <Typography variant="body1">
                  {t("noAvailableCircuits")}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      </Modal>
    </>
  );
}
